import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtIDR, fmtNum, FUEL_META, todayISO } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ExcelImportButtons } from "@/components/ExcelImportButtons";

const Penjualan = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fuel_code: "pertalite", liters: "", date: todayISO(), payment_method: "cash", note: "" });

  const load = () => api.get("/sales").then(r => setRows(r.data));
  useEffect(() => { load(); api.get("/fuels").then(r => setFuels(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sales", { ...form, liters: parseFloat(form.liters) });
      toast.success("Transaksi tersimpan");
      setOpen(false);
      setForm({ fuel_code: "pertalite", liters: "", date: todayISO(), payment_method: "cash", note: "" });
      load();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus transaksi ini?")) return;
    await api.delete(`/sales/${id}`); toast.success("Terhapus"); load();
  };

  const selectedFuel = fuels.find(f => f.code === form.fuel_code);
  const estTotal = selectedFuel ? (parseFloat(form.liters || 0) * selectedFuel.sell_price) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Transaksi Penjualan</h1>
          <p className="text-sm text-slate-500 mt-1">Input penjualan BBM per shift</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExcelImportButtons endpoint="/import/sales.xlsx" templateEndpoint="/import/sales-template.xlsx" filename="sales_template.xlsx" onDone={load} testidPrefix="sales-import" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-sales-button" className="bg-[#E30613] hover:bg-[#C8040F]"><Plus className="w-4 h-4 mr-2" />Tambah Penjualan</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Input Transaksi Penjualan</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label>Jenis BBM</Label>
                <Select value={form.fuel_code} onValueChange={(v) => setForm(f => ({ ...f, fuel_code: v }))}>
                  <SelectTrigger data-testid="sale-fuel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fuels.map(f => <SelectItem key={f.code} value={f.code}>{f.name} — {fmtIDR(f.sell_price)}/L</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Liter</Label>
                  <Input data-testid="sale-liters-input" type="number" step="0.01" value={form.liters} onChange={(e) => setForm(f => ({ ...f, liters: e.target.value }))} required />
                </div>
                <div className="space-y-2">
                  <Label>Tanggal</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Metode Pembayaran</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm(f => ({ ...f, payment_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="edc">EDC / Debit</SelectItem>
                    <SelectItem value="qris">QRIS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-lg bg-slate-50 p-3 flex items-center justify-between">
                <span className="text-sm text-slate-600">Estimasi Total</span>
                <span className="text-xl font-extrabold font-mono-fig text-[#E30613]">{fmtIDR(estTotal)}</span>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="sale-submit-button" className="bg-[#E30613] hover:bg-[#C8040F]">Simpan</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold">Daftar Transaksi</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>BBM</TableHead>
                <TableHead className="text-right">Liter</TableHead>
                <TableHead className="text-right">Harga/L</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead>Operator</TableHead>
                {user?.role === "admin" && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => {
                const m = FUEL_META[r.fuel_code];
                return (
                  <TableRow key={r.id} data-testid={`sale-row-${r.id}`}>
                    <TableCell className="font-mono-fig text-sm">{r.date}</TableCell>
                    <TableCell><Badge className={`${m.bg} ${m.text} border ${m.border}`}>{m.name}</Badge></TableCell>
                    <TableCell className="text-right font-mono-fig">{fmtNum(r.liters)}</TableCell>
                    <TableCell className="text-right font-mono-fig">{fmtIDR(r.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono-fig font-bold">{fmtIDR(r.total)}</TableCell>
                    <TableCell className="uppercase text-xs">{r.payment_method}</TableCell>
                    <TableCell className="text-sm">{r.operator_name}</TableCell>
                    {user?.role === "admin" && (
                      <TableCell>
                        <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`delete-sale-${r.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Belum ada transaksi</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Penjualan;
